// API base URL — ?env=staging switches to staging server
const params = new URLSearchParams(window.location.search);
const isStaging = params.get('env') === 'staging';

const API_BASE = import.meta.env.VITE_API_BASE ||
  (isStaging ? 'https://staging.pipeup2.live.eu.meteorapp.com' : 'https://api.pipeup.live');

export const API = {
  schedule: (pipeName) => `${API_BASE}/api/lightshow/schedule/${pipeName}`,
  script: (scriptId, version) => `${API_BASE}/api/lightshow/script/${scriptId}${version ? `?v=${version}` : ''}`,
  time: () => `${API_BASE}/api/lightshow/time`,
  participant: {
    register: () => `${API_BASE}/api/lightshow/participant/register`,
    status: () => `${API_BASE}/api/lightshow/participant/status`,
    telemetry: () => `${API_BASE}/api/lightshow/participant/telemetry`,
    heartbeat: () => `${API_BASE}/api/lightshow/participant/heartbeat`,
    zone: () => `${API_BASE}/api/lightshow/participant/zone`,
    optout: () => `${API_BASE}/api/lightshow/participant/optout`,
  },
};

// Base URL of the main app — clients redirect here after show ends
export const PIPE_BASE_URL = import.meta.env.VITE_PIPE_BASE_URL ||
  (isStaging ? 'https://staging.pipeup2.live.eu.meteorapp.com' : 'https://app.pipeup.live');

export const SCHEDULE_POLL_INTERVAL = 2000; // ms
export const TIME_SYNC_INTERVAL = 30000; // ms
// #319: 5 (was 3) — more round trips give the min-RTT offset selector a better
// chance of catching a low-asymmetry sample, which shrinks the cross-device p95
// tail. The time endpoint is served at the CloudFront edge (zero origin load),
// so extra samples cost nothing on the backend.
export const TIME_SYNC_SAMPLES = 5;

// Participant status-ping jitter. `playing` and `completed` are SYNCHRONISED
// across all clients (every device hits play/finish at the same instant), so a
// tight jitter creates a thundering-herd POST burst on the participant
// write-path (~clients / jitterSeconds per second). These are best-effort
// telemetry, so we spread them wide. At 20k clients: 20s → ~1k/s (vs 3s →
// ~6.7k/s). register/loaded/ready/zone are NOT synchronised (they fire as
// clients arrive) so they keep their natural spread. Proper fix: batch via the
// realtime gateway — see .claude/inbox.md → "Move lightshow participant
// tracking to the realtime gateway".
export const PLAYING_STATUS_JITTER_MS = 20000;
// Retired with MM #111: the post-show redirect no longer waits for the batch
// flush, because flushBeacon sends the completed ping via sendBeacon instead.
// The wait cost the entire return audience — see App.jsx.
// export const COMPLETED_STATUS_JITTER_MS = 12000;

// Participant batching via the realtime gateway. register/status/telemetry are
// buffered and flushed on a per-client rolling timer over a Socket.io
// `participant:batch` event — naturally desynchronised (each client's flush
// phase is randomised), so the synchronised playing/completed bursts no longer
// hit the Galaxy write-path. Falls back to the jittered HTTP endpoints when the
// socket isn't connected. See .claude/inbox.md → "Move lightshow participant
// tracking to the realtime gateway".
export const PARTICIPANT_BATCH_FLUSH_MS = 10000;

// Periodic `playing` heartbeat while the choreo runs. Feeds server-side
// peak-concurrent sampling (the trustworthy headline metric) — cheap now that
// it rides the batch buffer. Jittered ±50% per client so it stays desynced.
export const PLAYING_HEARTBEAT_MS = 25000;

// Realtime-token endpoint — issues the gateway JWT + endpoint URL.
export const REALTIME_TOKEN_URL = (pipeId) =>
  `${API_BASE}/api/auth/realtime-token?pipeId=${pipeId}`;
